import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis
} from 'recharts';
import { api, fmtMin } from '@/core';
import type { Summary, SummaryDaily } from '@/types';

type DataPoint = { date: string; minutes: number; isNew: boolean };
type ChartType = 'line' | 'area';

const upColor = '#10b981'; // emerald-500
const downColor = '#ef4444'; // red-500
const flatColor = '#9ca3af'; // gray-400
const gridColor = 'hsl(0 0% 60% / 0.2)';
const PERIODS = [7, 14, 30];
const NEUTRAL_PCT = 0.5;

function formatDay(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Fill in any missing days so the chart is a continuous series.
function buildDailyList(daily: SummaryDaily[], range: number): DataPoint[] {
  const map: Record<string, number> = {};
  daily.forEach(d => {
    map[d.date] = d.minutes;
  });
  const dates = new Set<string>(Object.keys(map));
  for (let i = range - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    dates.add(d.toISOString().slice(0, 10));
  }
  const list: DataPoint[] = [];
  dates.forEach(date => list.push({ date, minutes: map[date] || 0, isNew: false }));
  list.sort((a, b) => (a.date < b.date ? -1 : 1));
  if (list.length) list[list.length - 1].isNew = true;
  return list;
}

function effortStats(vals: number[]) {
  const sum = vals.reduce((a, b) => a + b, 0);
  const first = vals[0] ?? 0;
  const last = vals[vals.length - 1] ?? 0;
  const prev = vals[vals.length - 2] ?? first;
  const net = last - first;
  return {
    sum,
    net,
    pct: first ? (net / first) * 100 : 0,
    step: last - prev,
    peak: vals.length ? Math.max(...vals) : 0,
    low: vals.length ? Math.min(...vals) : 0,
    avg: vals.length ? sum / vals.length : 0
  };
}

// ── Small UI primitives ──────────────────────────────────────

function Switch({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: string;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={[
        'relative inline-flex h-6 w-11 items-center rounded-full transition cursor-pointer',
        checked
          ? 'bg-gray-300 dark:bg-gray-500 shadow-[0_0_8px_rgba(148,163,184,0.5)]'
          : 'bg-gray-400/60 dark:bg-gray-600 shadow-inner'
      ].join(' ')}
      aria-pressed={checked}
      aria-label={label || 'Toggle'}
    >
      <span
        className={[
          'inline-block h-5 w-5 transform rounded-full bg-white transition',
          checked ? 'translate-x-5' : 'translate-x-1'
        ].join(' ')}
      />
    </button>
  );
}

function Select({
  value,
  onChange,
  options
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}): ReactElement {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="rounded-lg border border-black/10 dark:border-white/15 bg-white/70 dark:bg-white/10 px-3 py-1.5 text-sm text-gray-900 dark:text-white backdrop-blur cursor-pointer focus:outline-none focus:ring-1 focus:ring-emerald-400/40"
    >
      {options.map(o => (
        <option key={o.value} value={o.value} className="text-gray-900">
          {o.label}
        </option>
      ))}
    </select>
  );
}

function ChartTooltipContent({
  active,
  payload,
  label
}: {
  active?: boolean;
  payload?: unknown[];
  label?: string;
}): ReactElement | null {
  if (!active || !payload?.length) return null;
  const minutes = (payload[0] as { value?: number } | undefined)?.value ?? 0;
  return (
    <div className="rounded-lg border border-black/10 bg-white/80 dark:border-white/20 dark:bg-black/60 px-3 py-2 text-xs text-gray-900 dark:text-gray-100 shadow-md backdrop-blur">
      <div className="font-medium">{formatDay(label || '')}</div>
      <div className="opacity-80">{fmtMin(minutes)}</div>
    </div>
  );
}

// ── Main chart ───────────────────────────────────────────────

export function DailyEffortChart({ className = '' }: { className?: string }): ReactElement {
  const [days, setDays] = useState<number>(14);
  const [chartType, setChartType] = useState<ChartType>('area');
  const [data, setData] = useState<DataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchSummary = useCallback(async (range: number): Promise<void> => {
    try {
      const s = await api<Summary>(`/api/summary?days=${range}`);
      setData(buildDailyList(s.daily || [], s.range_days || range));
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load effort');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchSummary(days);
  }, [days, fetchSummary]);

  const stats = useMemo(() => effortStats(data.map(d => d.minutes)), [data]);
  const hasEffort = data.some(d => d.minutes > 0);

  const isDown = Math.abs(stats.pct) >= NEUTRAL_PCT && stats.net < 0;
  const isUp = Math.abs(stats.pct) >= NEUTRAL_PCT && stats.net > 0;
  const trendColor = isDown ? downColor : isUp ? upColor : flatColor;
  const trendArrow = isDown ? '▼' : isUp ? '▲' : '→';
  const pctLabel = `${Math.abs(stats.pct).toFixed(1)}%`;
  const stepLabel = `${stats.step >= 0 ? '+' : '−'}${fmtMin(Math.abs(stats.step))}`;

  const CustomDot = (props: any): ReactElement | null => {
    const { cx, cy, payload } = props;
    if (!payload || !payload.isNew) return null;
    return <circle cx={cx} cy={cy} r={5} fill={trendColor} stroke="none" className="animate-pulse" />;
  };
  const CustomActiveDot = (props: any): ReactElement | null => {
    const { cx, cy } = props;
    return <circle cx={cx} cy={cy} r={5} fill={trendColor} stroke="currentColor" strokeWidth={2} />;
  };

  return (
    <div
      className={[
        'relative rounded-2xl border border-black/10 dark:border-white/10 bg-white/40 dark:bg-white/5 backdrop-blur-xl',
        'p-4 sm:p-5',
        className
      ].join(' ')}
    >
      {/* Controls */}
      <div className="relative z-10 flex items-center justify-between gap-3 flex-wrap">
        <Select
          value={String(days)}
          onChange={v => setDays(Number(v))}
          options={PERIODS.map(p => ({ value: String(p), label: `Last ${p} days` }))}
        />
        <Switch
          checked={chartType === 'line'}
          onChange={on => setChartType(on ? 'line' : 'area')}
          label="Line chart"
        />
      </div>

      {/* Ticker */}
      <div className="relative z-10 mt-5 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100">{fmtMin(stats.sum)}</div>
          <div
            className={`mt-1 flex items-center ${isDown ? 'text-red-500' : isUp ? 'text-emerald-500' : 'text-gray-500 dark:text-gray-400'}`}
          >
            <span className="mr-1">{trendArrow}</span>
            <span className="font-medium">{pctLabel}</span>
            <span className="mx-2 opacity-40">·</span>
            <span className="opacity-80">{stepLabel} today</span>
          </div>
          {error && <div className="mt-1 text-xs text-red-500">{error}</div>}
        </div>
        <div className="text-xs text-gray-600 dark:text-gray-400 flex gap-3 flex-wrap">
          <span>
            <b className="text-gray-900 dark:text-gray-100">{fmtMin(stats.peak)}</b> peak
          </span>
          <span>
            <b className="text-gray-900 dark:text-gray-100">{fmtMin(stats.low)}</b> low
          </span>
          <span>
            <b className="text-gray-900 dark:text-gray-100">{fmtMin(Math.round(stats.avg))}</b> avg
          </span>
        </div>
      </div>

      {/* Chart */}
      <div className="relative z-10 mt-4 h-[280px] sm:h-[320px]">
        {loading ? (
          <div className="h-full w-full flex items-center justify-center">
            <div className="animate-pulse text-sm text-gray-500 dark:text-gray-400">Loading effort…</div>
          </div>
        ) : !hasEffort ? (
          <div className="h-full w-full flex flex-col items-center justify-center text-center gap-2">
            <div className="text-2xl">⏱</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">No effort logged yet.</p>
            <p className="text-xs opacity-70">Start the timer or log a session to see it here.</p>
          </div>
        ) : (
          <div className="h-full w-full rounded-xl border border-black/10 dark:border-white/10 bg-white/5 dark:bg-white/5 backdrop-blur-lg p-2 sm:p-3">
            <ResponsiveContainer width="100%" height="100%">
              {chartType === 'line' ? (
                <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridColor} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDay}
                    tick={{ fontSize: 12, fill: 'currentColor' }}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={28}
                  />
                  <YAxis
                    domain={[0, 'auto']}
                    tick={{ fontSize: 12, fill: 'currentColor' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={value => fmtMin(Number(value))}
                  />
                  <ReTooltip content={<ChartTooltipContent />} />
                  <Line
                    type="monotone"
                    dataKey="minutes"
                    stroke={upColor}
                    strokeWidth={2}
                    style={{ filter: 'drop-shadow(0 0 5px rgba(16,185,129,0.6))' }}
                    dot={props => <CustomDot {...props} />}
                    activeDot={props => <CustomActiveDot {...props} />}
                    isAnimationActive={false}
                  />
                </LineChart>
              ) : (
                <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridColor} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDay}
                    tick={{ fontSize: 12, fill: 'currentColor' }}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={28}
                  />
                  <YAxis
                    domain={[0, 'auto']}
                    tick={{ fontSize: 12, fill: 'currentColor' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={value => fmtMin(Number(value))}
                  />
                  <ReTooltip content={<ChartTooltipContent />} />
                  <Area
                    type="monotone"
                    dataKey="minutes"
                    stroke={upColor}
                    fill={upColor}
                    fillOpacity={0.18}
                    strokeWidth={2}
                    style={{ filter: 'drop-shadow(0 0 5px rgba(16,185,129,0.6))' }}
                    dot={props => <CustomDot {...props} />}
                    activeDot={props => <CustomActiveDot {...props} />}
                    isAnimationActive={false}
                  />
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}